import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Inbox, CheckCircle2, AlertOctagon, Trash2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const fmtAge = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const sec = (Date.now() - d.getTime()) / 1000;
    if (sec < 60) return `${Math.round(sec)}s fa`;
    if (sec < 3600) return `${Math.round(sec / 60)}m fa`;
    if (sec < 86400) return `${(sec / 3600).toFixed(1)}h fa`;
    return `${(sec / 86400).toFixed(1)}g fa`;
  } catch {
    return "—";
  }
};

/**
 * Admin panel: GHL Write Queue.
 * Mostra il numero di scritture pending/success/failed e permette di
 * forzare il retry del worker. Le scritture in queue sono PUT/DELETE
 * idempotenti che hanno fallito tutti i retry di tenacity → vengono
 * ritentate ogni 60s dal job ghl_write_queue_worker.
 */
export default function GhlWriteQueuePanel() {
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [data, setData] = useState(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/admin/ghl-write-queue/status`, {
        withCredentials: true,
      });
      setData(resp);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Errore caricamento queue");
    } finally {
      setLoading(false);
    }
  }, []);

  const retryAll = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/admin/ghl-write-queue/retry-all`, {}, { withCredentials: true });
      toast.success("Worker queue eseguito");
      await fetchStatus(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore retry queue");
    } finally {
      setRetrying(false);
    }
  };

  const dismiss = async (qid) => {
    if (!window.confirm("Marcare questa scrittura come gestita manualmente? Non verrà più ritentata.")) return;
    try {
      await axios.post(`${API}/admin/ghl-write-queue/dismiss/${qid}`, {}, { withCredentials: true });
      toast.success("Job dismissed");
      await fetchStatus(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore dismiss");
    }
  };

  useEffect(() => {
    fetchStatus(false);
    const id = setInterval(() => fetchStatus(true), 60000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const stats = data?.stats || { pending: 0, success_total: 0, failed_permanent: 0 };
  const hasAlert = stats.pending > 0 || stats.failed_permanent > 0;

  return (
    <div
      className={`bg-white border rounded-2xl p-4 space-y-3 ${
        hasAlert ? "border-amber-300" : "border-slate-200"
      }`}
      data-testid="ghl-write-queue-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            hasAlert ? "bg-amber-100" : "bg-emerald-100"
          }`}>
            <Inbox className={`w-4 h-4 ${hasAlert ? "text-amber-700" : "text-emerald-700"}`} />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">Write Queue GHL</div>
            <div className="text-[10px] text-slate-500">Scritture accodate per retry automatico (q60s)</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={retryAll}
            disabled={retrying || loading || stats.pending === 0}
            className="h-7 px-2 text-[11px]"
            data-testid="ghl-write-queue-retry-btn"
            title="Esegue subito il worker della queue"
          >
            {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            <span className="ml-1">Retry ora</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchStatus(false)}
            disabled={loading}
            className="h-7 px-2"
            data-testid="ghl-write-queue-reload-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-amber-700">{stats.pending}</div>
              <div className="text-[10px] text-amber-800">In coda</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-emerald-700">{stats.success_total}</div>
              <div className="text-[10px] text-emerald-800">Riusciti</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-red-700">{stats.failed_permanent}</div>
              <div className="text-[10px] text-red-800">Falliti perm.</div>
            </div>
          </div>

          {stats.pending > 0 && data.pending?.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] font-semibold text-amber-800">In coda ({data.pending.length}):</div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {data.pending.slice(0, 8).map((q) => (
                  <div key={q._id} className="bg-amber-50 border border-amber-200 rounded p-1.5 text-[10px] flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate">
                        <span className="font-bold">{q.method}</span> {q.endpoint}
                      </div>
                      <div className="text-amber-700">
                        Tent. {q.attempts}/{q.max_attempts || 10} · {fmtAge(q.created_at)}
                        {q.context?.label && <span> · {q.context.label}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.failed_permanent > 0 && data.failed_permanent?.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] font-semibold text-red-800 flex items-center gap-1">
                <AlertOctagon className="w-3 h-3" />
                Falliti permanentemente ({data.failed_permanent.length}):
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {data.failed_permanent.slice(0, 8).map((q) => (
                  <div key={q._id} className="bg-red-50 border border-red-200 rounded p-1.5 text-[10px] flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate">
                        <span className="font-bold">{q.method}</span> {q.endpoint}
                      </div>
                      <div className="text-red-700">
                        {q.attempts}/{q.max_attempts} tent · {fmtAge(q.created_at)}
                        {q.context?.label && <span> · {q.context.label}</span>}
                      </div>
                      {q.last_error && (
                        <div className="text-red-600 italic truncate" title={q.last_error}>
                          {q.last_error.substring(0, 80)}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismiss(q._id)}
                      className="h-5 px-1 text-[10px] text-red-700 hover:bg-red-100"
                      title="Marca come risolto manualmente"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats.pending === 0 && stats.failed_permanent === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px] text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-3 h-3" />
              Nessuna scrittura in coda. GHL sta rispondendo correttamente.
            </div>
          )}

          <div className="text-[10px] text-slate-500 italic">
            Solo le scritture idempotenti (PUT/PATCH/DELETE) vengono accodate quando GHL fallisce.
            Le creazioni POST falliscono fast per evitare duplicati.
          </div>
        </>
      )}
    </div>
  );
}
